(function () {
  'use strict';

  const TRANSITION_MS = 320;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canAutoFocus = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

  const state = {
    isOpen: false,
    editorPreviewOpen: false,
    selectedSectionId: null,
    lastFocus: null,
    scrollY: 0,
    scrollLocked: false,
    closeTimer: null,
  };

  let abortController = null;

  function getDom() {
    return {
      popup: document.querySelector('[data-support-popup]'),
      panel: document.querySelector('[data-support-panel]'),
      overlay: document.querySelector('[data-support-overlay]'),
    };
  }

  function getSectionRoot(sectionId) {
    if (!sectionId) return null;
    return document.getElementById(`shopify-section-${sectionId}`);
  }

  function isSupportSection(sectionId) {
    const root = getSectionRoot(sectionId);
    return Boolean(root && root.classList.contains('support-popup-section'));
  }

  function isPopupEnabled() {
    const popup = getDom().popup;
    if (!popup) return false;
    return popup.getAttribute('data-support-enabled') !== 'false';
  }

  const ScrollLock = {
    getScrollbarWidth() {
      return window.innerWidth - document.documentElement.clientWidth;
    },

    lock() {
      if (state.scrollLocked) return;

      state.scrollY = window.scrollY || window.pageYOffset || 0;
      const scrollbarWidth = this.getScrollbarWidth();

      document.documentElement.classList.add('support-popup-open');
      document.body.classList.add('support-popup-open');
      document.body.style.top = `-${state.scrollY}px`;
      document.body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';

      state.scrollLocked = true;
    },

    unlock() {
      if (!state.scrollLocked) return;

      document.documentElement.classList.remove('support-popup-open');
      document.body.classList.remove('support-popup-open');
      document.body.style.top = '';
      document.body.style.paddingRight = '';

      window.scrollTo(0, state.scrollY);
      state.scrollLocked = false;
    },
  };

  function focusPopup() {
    if (!canAutoFocus) return;

    const popup = getDom().popup;
    if (!popup) return;

    const closeBtn = popup.querySelector('[data-support-close]');
    if (closeBtn instanceof HTMLElement) {
      closeBtn.focus({ preventScroll: true });
      return;
    }

    const panel = getDom().panel;
    if (panel instanceof HTMLElement) {
      panel.focus({ preventScroll: true });
    }
  }

  function openPopup(trigger, options) {
    const popup = getDom().popup;
    const panel = getDom().panel;
    const preview = options?.preview === true;

    if (!popup || !panel) {
      return false;
    }

    if (!preview && !isPopupEnabled()) {
      return false;
    }

    if (state.isOpen) {
      return true;
    }

    if (state.closeTimer) {
      window.clearTimeout(state.closeTimer);
      state.closeTimer = null;
    }

    if (trigger instanceof HTMLElement) {
      state.lastFocus = trigger;
    } else if (!preview) {
      state.lastFocus = document.activeElement;
    }

    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');
    panel.setAttribute('aria-hidden', 'false');

    window.requestAnimationFrame(function () {
      popup.classList.add('is-open');

      window.requestAnimationFrame(function () {
        ScrollLock.lock();
        focusPopup();
      });
    });

    state.isOpen = true;
    state.editorPreviewOpen = preview;
    return true;
  }

  function closePopup(options) {
    const force = options?.force === true;
    const popup = getDom().popup;
    const panel = getDom().panel;

    if (!popup) {
      ScrollLock.unlock();
      return;
    }

    if (isDesignMode && state.editorPreviewOpen && !force && state.selectedSectionId) {
      return;
    }

    if (!state.isOpen) {
      ScrollLock.unlock();
      return;
    }

    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    panel?.setAttribute('aria-hidden', 'true');
    state.isOpen = false;
    state.editorPreviewOpen = false;
    ScrollLock.unlock();

    const delay = prefersReducedMotion ? 0 : TRANSITION_MS;
    state.closeTimer = window.setTimeout(function () {
      if (!state.isOpen) {
        popup.hidden = true;
      }

      if (state.lastFocus instanceof HTMLElement && !isDesignMode) {
        state.lastFocus.focus({ preventScroll: true });
      }

      state.closeTimer = null;
    }, delay);
  }

  function trapFocus(event) {
    const panel = getDom().panel;
    if (!state.isOpen || !panel || event.key !== 'Tab') return;

    const focusable = panel.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  function onDocumentClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openTrigger = target.closest('[data-support-popup-open]');
    if (openTrigger) {
      event.preventDefault();
      openPopup(openTrigger, { preview: false });
      return;
    }

    if (target.closest('[data-support-overlay]') && state.isOpen) {
      event.preventDefault();
      closePopup({ force: true });
      return;
    }

    if (target.closest('[data-support-close]') && state.isOpen) {
      event.preventDefault();
      closePopup({ force: true });
    }
  }

  function onDocumentKeydown(event) {
    if (event.key === 'Escape' && state.isOpen) {
      event.preventDefault();
      closePopup({ force: true });
      return;
    }

    trapFocus(event);
  }

  function onSectionSelect(event) {
    const sectionId = event.detail?.sectionId;
    if (!sectionId || !isSupportSection(sectionId)) return;

    state.selectedSectionId = sectionId;
    openPopup(null, { preview: true });
  }

  function onSectionDeselect(event) {
    const sectionId = event.detail?.sectionId;
    if (!sectionId || !isSupportSection(sectionId)) return;

    state.selectedSectionId = null;
    closePopup({ force: true });
  }

  function onSectionLoad(event) {
    const sectionId = event.detail?.sectionId;
    if (!sectionId || !isSupportSection(sectionId)) return;

    if (state.selectedSectionId === sectionId) {
      window.requestAnimationFrame(function () {
        openPopup(null, { preview: true });
      });
    }
  }

  function onSectionUnload(event) {
    const sectionId = event.detail?.sectionId;
    if (!sectionId || !isSupportSection(sectionId)) return;

    if (state.isOpen) {
      closePopup({ force: true });
    }
  }

  function destroy() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  function bindEvents() {
    destroy();
    abortController = new AbortController();
    const signal = abortController.signal;

    document.addEventListener('click', onDocumentClick, { signal });
    document.addEventListener('keydown', onDocumentKeydown, { signal });

    if (isDesignMode) {
      document.addEventListener('shopify:section:select', onSectionSelect, { signal });
      document.addEventListener('shopify:section:deselect', onSectionDeselect, { signal });
      document.addEventListener('shopify:section:load', onSectionLoad, { signal });
      document.addEventListener('shopify:section:unload', onSectionUnload, { signal });
    }
  }

  function init() {
    bindEvents();
  }

  window.MorldoroSupportPopup = {
    openPopup,
    closePopup,
    reinit: init,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
